import React, { useRef } from 'react';
import { useInView } from 'framer-motion';
import { motion } from '../../lib/motionShim';
import LiveScreen from '../LiveScreen';
import GhostNumeral from '../GhostNumeral';
import Chips from '../Chips';

/** 03 · FUEL — the India wedge. Dal, oats, whey, milk, paneer — on screen. */
const FoodSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-120px' });

  const rise = (delay) => ({
    initial: { opacity: 0, y: 26 },
    animate: isInView ? { opacity: 1, y: 0 } : {},
    transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] },
  });

  return (
    <section
      id="food"
      ref={ref}
      className="relative py-24 md:py-32 bg-ston-bg-alt overflow-hidden"
      data-testid="food-section"
    >
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 -right-32 w-[560px] h-[560px] rounded-full" style={{ background: 'radial-gradient(closest-side, rgba(249,115,22,0.06), rgba(249,115,22,0.030) 55%, transparent 78%)' }} />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24 items-center">

          {/* Copy */}
          <div>
            <motion.span className="section-label inline-block" {...rise(0)}>
              03 · FUEL
            </motion.span>
            <motion.h2
              className="mt-4 font-outfit font-extrabold italic uppercase leading-[1.02]"
              style={{ fontSize: 'clamp(30px, 4vw, 52px)', letterSpacing: '-0.025em' }}
              {...rise(0.1)}
            >
              <span className="block text-ston-text">Your daal-chawal</span>
              <span
                className="block"
                style={{
                  background: 'linear-gradient(100deg, #fdba74 0%, #fb923c 50%, #f59e0b 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                has calories too.
              </span>
            </motion.h2>
            <motion.p
              className="mt-6 text-[17px] text-ston-text-secondary leading-relaxed max-w-[52ch]"
              {...rise(0.2)}
            >
              Every app out there thinks you eat oats and chicken breast. STON.FIT
              knows poha, upma, paratha, paneer bhurji, vada pav — logged with real
              macros. Then ask it what to eat next: it knows what you've eaten, what
              you have left, and what your last lab report said.
            </motion.p>
            <Chips items={['Indian food database', 'Search it', 'Snap it', 'Say it']} />
          </div>

          {/* Device */}
          <motion.div
            className="flex justify-center lg:justify-end"
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="relative">
              <GhostNumeral
                className="absolute -top-14 -right-8 md:-right-16"
                stroke="rgba(251,146,60,0.16)"
                size="clamp(120px, 12vw, 190px)"
              >
                845
              </GhostNumeral>
              <LiveScreen
                dir={-1}
                src="/screens/nutrition-meals.webp"
                alt="A logged Indian meal — dal, oats, whey protein, milk and paneer totalling 845 kcal"
                width={300}
                glowColor="rgba(251,146,60,0.2)"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FoodSection;
